<template>
  <div class="plan-trail-elevation" v-if="chartData">
    <ElevationProfile
      :data="chartData"
      :hover-distance-meters="mapHoverDistance"
      :title="panelTitle || ''"
      :subtitle="panelSubtitle"
      :day-markers="chartData.dayMarkers"
      pinned-tooltip
      collapsible
      :expandable="false"
      @hover="onHover"
      @hoverEnd="onHoverEnd"
      @pointClick="onPointClick"
    >
      <template #headerLeft>
        <!-- Back to the overview ("none selected") state — mirrors
             deactivateRoutes in PlanTrailRoutes so the map also returns to the
             full-colour all-routes view -->
        <button
          v-if="showBackButton"
          class="back-btn"
          type="button"
          title="Back to all routes"
          aria-label="Deselect route and show all routes"
          @click="deselectRoute"
        >
          <span class="material-icons">arrow_back</span>
        </button>
      </template>
    </ElevationProfile>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from 'vue';
import { useStore } from 'vuex';
import mapboxgl from 'mapbox-gl';
import ElevationProfile from '../../components/ElevationProfile.vue';
import { ServiceProvider } from '../services/ServiceProvider';
import { MarkerFeature } from '../../../utils/models/MarkerFeature';
import type { ElevationChartData } from '../../../utils/elevation/buildElevationChartData';

interface CoordToLineStringEntry {
  routeKey: string;
  lineStringIndex: number;
  coordIndex: number;
}

// The planner's createChartData returns the shared ElevationChartData plus
// planner-specific extras: the reverse map used to resolve a chart point back
// to route geometry (hover-sync and click→insert-control-point), and the
// day-boundary markers drawn on the combined overview profile.
type PlanChartData = ElevationChartData & {
  coordToLineString: CoordToLineStringEntry[];
  dayMarkers: Array<{ x: number; day: number; label: string }>;
};

export default defineComponent({
  name: 'PlanTrailElevation',
  components: { ElevationProfile },
  setup() {
    const store = useStore();

    // Fallback marker used only when the map service is unavailable.
    const fallbackHoverMarker = ref<mapboxgl.Marker | null>(null);

    const chartData = computed<PlanChartData | null>(
      () => store.getters['planTrail/createChartData']
    );
    // mapHoverDistance is stored in METRES; the shared component's reverse-sync
    // prop expects metres, so it is passed straight through.
    const mapHoverDistance = computed<number | null>(
      () => store.state.planTrail.mapHoverDistance
    );
    const hasActiveRoute = computed<boolean>(() => !!store.getters['planTrail/activeRoute']);
    const hasMultipleRoutes = computed<boolean>(
      () => store.getters['planTrail/routesArray'].length > 1
    );
    const panelTitle = computed<string | null>(() => {
      const activeRoute = store.getters['planTrail/activeRoute'];
      if (activeRoute) return activeRoute.name || null;
      // Overview mode — the chart combines every route
      return hasMultipleRoutes.value ? 'All routes' : null;
    });
    // Clicking inserts a control point only while a route is selected; the
    // overview profile is read-only. Kept short — the header is narrow and
    // already carries the title + distance/gain/loss stats.
    const panelSubtitle = computed<string>(() =>
      hasActiveRoute.value ? 'Click to insert a point' : 'Hover to track location'
    );
    // "Back to all routes" is only meaningful when there's more than one
    // route/day to go back TO — a single-route plan has nothing to overview.
    const showBackButton = computed<boolean>(
      () => hasActiveRoute.value && hasMultipleRoutes.value
    );

    // Back to the overview ("none selected") state
    const deselectRoute = () => {
      store.commit('planTrail/clearActiveRoute');
      store.dispatch('planTrail/addAllRoutesToMap');
    };

    const createFallbackHoverMarkerEl = () => {
      const el = document.createElement('div');
      el.style.width = '12px';
      el.style.height = '12px';
      el.style.borderRadius = '50%';
      el.style.backgroundColor = '#bbbbbb';
      el.style.border = '2px solid #ffffff';
      return el;
    };

    // Place/refresh the styled hover marker via the map service (preferred), with
    // a raw mapboxgl marker fallback if the service isn't available.
    const ensureHoverMarker = (lng: number, lat: number) => {
      try {
        const services = ServiceProvider.getInstance().getServices();
        const { mapService } = services;

        if (mapService.hasSource('hover-marker')) {
          mapService.updateMarkerPosition('hover-marker', [lng, lat]);
          return;
        }

        MarkerFeature.fromCoordinates([lng, lat], true)
          .then((markerFeature) =>
            mapService.addMarker('hover-marker', markerFeature, {
              isActive: true,
              opacity: 1,
            })
          )
          .catch(() => {});
      } catch (_) {
        if (window.map) {
          if (!fallbackHoverMarker.value) {
            fallbackHoverMarker.value = new mapboxgl.Marker({
              element: createFallbackHoverMarkerEl(),
            });
          }
          fallbackHoverMarker.value.setLngLat([lng, lat]).addTo(window.map);
        }
      }
    };

    const removeHoverMarker = () => {
      try {
        const services = ServiceProvider.getInstance().getServices();
        services.mapService.removeMarker('hover-marker');
      } catch (_) {
        if (fallbackHoverMarker.value) {
          fallbackHoverMarker.value.remove();
          fallbackHoverMarker.value = null;
        }
      }
    };

    const clearHoverState = () => {
      store.commit('planTrail/setMapHoverDistance', null);
      store.commit('planTrail/setNearLine', false);
      store.commit('planTrail/setHoverPoint', null);
      removeHoverMarker();
    };

    // Chart hover → drive the map hover marker + Vuex hover state. `index` is the
    // sampled point index, aligned with chartData.coordToLineString. Each entry
    // carries its owning route (overview mode charts all routes combined; with a
    // selection it's just the active one).
    const onHover = ({
      index,
      lngLat,
    }: {
      index: number;
      lngLat: [number, number];
      distanceMeters: number;
    }) => {
      const data = chartData.value;
      const activeRoute = store.getters['planTrail/activeRoute'];
      const entry = data?.coordToLineString?.[index];
      const routeForPoint = entry?.routeKey
        ? store.state.planTrail.routes[entry.routeKey]
        : activeRoute;

      if (!data || !routeForPoint || !entry) {
        clearHoverState();
        return;
      }

      const { lineStringIndex } = entry;
      const lineString = routeForPoint.lineStrings[lineStringIndex];
      if (!lineString?.lineStringGeometry) {
        clearHoverState();
        return;
      }

      const [lon, lat] = lngLat;
      // Use the pre-computed distance (metres) for this sampled point.
      const distanceMeters = data.distancesMeters?.[index] ?? 0;
      const lineStringId = lineString?.lineStringProperties?.id;

      store.commit('planTrail/setMapHoverDistance', distanceMeters);
      // Insert-control-point preview only applies while editing a selected
      // route; in overview mode the hover is read-only
      store.commit('planTrail/setNearLine', !!activeRoute);
      if (activeRoute && lineStringId) {
        store.commit('planTrail/setHoverPoint', {
          lng: lon,
          lat,
          lineStringId,
          insertIndex: lineStringIndex + 1,
        });
      } else {
        store.commit('planTrail/setHoverPoint', null);
      }

      ensureHoverMarker(lon, lat);
    };

    const onHoverEnd = () => {
      clearHoverState();
    };

    // Chart click. In overview mode (no route selected) the combined profile
    // is read-only for editing, so a click instead ACTIVATES the day the
    // clicked point belongs to — same as clicking that day in the left panel
    // (switchToDay also zooms the map to the day's routes). With a route
    // selected, a click inserts a control point at the clicked location,
    // resolving the sampled point back to its linestring + coordinate index
    // via coordToLineString.
    const onPointClick = ({
      index,
      lngLat,
    }: {
      index: number;
      lngLat: [number, number];
      distanceMeters: number;
    }) => {
      const data = chartData.value;
      const activeRoute = store.getters['planTrail/activeRoute'];
      const entry = data?.coordToLineString?.[index];
      if (!data || !entry) return;

      if (!activeRoute) {
        const route = entry.routeKey ? store.state.planTrail.routes[entry.routeKey] : null;
        if (route) {
          store.dispatch('planTrail/switchToDay', route.day ?? 1);
        }
        return;
      }

      if (!activeRoute.lineStrings.length) return;

      const { lineStringIndex, coordIndex } = entry;

      // insertIndex is lineStringIndex + 1 because linestring[i] connects
      // marker[i] to marker[i+1]; we insert the new marker after marker[i].
      // segmentIndex is coordIndex within that linestring.
      store.dispatch('planTrail/insertMarkerAtPoint', {
        point: lngLat,
        routeId: activeRoute.id,
        insertIndex: lineStringIndex + 1,
        segmentIndex: coordIndex,
      });
    };

    return {
      chartData,
      mapHoverDistance,
      panelTitle,
      panelSubtitle,
      showBackButton,
      deselectRoute,
      onHover,
      onHoverEnd,
      onPointClick,
    };
  },
});
</script>

<style scoped>
/* Position the shared elevation profile as a floating bottom-center panel,
   matching the planner layout. The panel's own chrome (background, rounding,
   collapse) is provided by <ElevationProfile>. */
.plan-trail-elevation {
  position: absolute;
  bottom: 3.2rem;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  max-width: calc(100vw - 680px); /* Account for side panels */
  z-index: 500;
}

/* Back-to-all-routes button, rendered in the shared panel's header via the
   headerLeft slot */
.back-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
}

.back-btn .material-icons {
  font-size: 18px;
}

@media (max-width: 768px) {
  .plan-trail-elevation {
    width: calc(100vw - 20px);
    max-width: calc(100vw - 20px);
    left: 10px;
    transform: none;
  }
}
</style>
